// frontend/src/hooks/useUnitPreferences.js
// Preferencias de unidades del usuario, con caché en módulo.
//
// Muchos componentes (cada PositionTile, cada fila de la vista lista) necesitan
// las mismas preferencias. Sin caché eso son decenas de peticiones idénticas por
// render del panel. Aquí se resuelve una sola vez y se comparte; `refreshUnitPreferences`
// invalida la caché cuando el usuario cambia sus unidades.

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { DEFAULT_UNIT_PREFERENCES } from '../utils/unitFormat';

let cachedPreferences = null;
let inFlight = null;
const listeners = new Set();

const notify = (prefs) => {
  listeners.forEach((listener) => {
    try {
      listener(prefs);
    } catch (_) {
      /* un suscriptor roto no puede tumbar a los demás */
    }
  });
};

export const loadUnitPreferences = async () => {
  if (cachedPreferences) return cachedPreferences;
  if (!inFlight) {
    inFlight = api.unitPreferences
      .getMyPreferences()
      .then((response) => {
        cachedPreferences = response?.data || DEFAULT_UNIT_PREFERENCES;
        return cachedPreferences;
      })
      .catch(() => {
        cachedPreferences = DEFAULT_UNIT_PREFERENCES;
        return cachedPreferences;
      })
      .finally(() => {
        inFlight = null;
        notify(cachedPreferences);
      });
  }
  return inFlight;
};

/** Invalida la caché tras guardar nuevas preferencias. */
export const refreshUnitPreferences = async () => {
  cachedPreferences = null;
  inFlight = null;
  const prefs = await loadUnitPreferences();
  notify(prefs);
  return prefs;
};

export const useUnitPreferences = () => {
  const [preferences, setPreferences] = useState(cachedPreferences);
  const [loading, setLoading] = useState(!cachedPreferences);

  useEffect(() => {
    let cancelled = false;
    const listener = (prefs) => {
      if (!cancelled) setPreferences(prefs);
    };
    listeners.add(listener);

    if (!cachedPreferences) {
      loadUnitPreferences().then((prefs) => {
        if (cancelled) return;
        setPreferences(prefs);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  const refresh = useCallback(() => refreshUnitPreferences(), []);

  return { preferences: preferences || DEFAULT_UNIT_PREFERENCES, loading, refresh, ready: !!preferences };
};

export default useUnitPreferences;
